// The Coordinator Workbench (`#workbench`, `#workbench/<sr_…>`; lane B5 S11, screens-walks §2.1): "4.2 Coordinator
// Workbench — Intakes, recruiter screens, HM interviews, Final Interview Loops and debriefs to schedule." One
// `scheduling.workbench` read draws the counts, the filters' result and the eight queue groups in workflow order, so
// "workbench counts reconcile to records" holds by construction: a group's count is the number of rows the server
// counted for it under the same filter. Completed and Cancelled start collapsed ("completed items close but remain
// historical."). A row's name cell is the link that opens the request beside the queue; its one action is DC-35's verb
// for the status. A seat that reaches the scheduling writes only through the audited override reads the queue as a view.
// Registered as a view (views/b5/register.ts), so it renders in MissionShell's content slot inside its one McRoot.

import { useEffect, useMemo, useState } from "react";
import type { WorkbenchRowOut } from "@/api/tools";
import { schedulingRemind, schedulingWorkbench } from "@/api/tools";
import { useRead, useWrite } from "@/api/useTool";
import { go, useRoute } from "@/router";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { NotFound } from "@/views/NotFound";
import {
  Banner, Button, Chip, Chips, CORE_LABELS, Due, EmptyState, ErrorStrip, FILTERBAR_LABELS, FINAL_LOOP_SCHEDULING_STATUS, Flex, KpiRow,
  KpiSelect, METRIC_LABELS, NAV_ITEM_LABELS, PageHeader, Paragraph, Pill, QueueGroup, RULE_LABELS, Recorded, Row, RowName,
  SCHEDULING_LABELS, SCHEDULING_STATUS, SLA_LABELS, SectionTitle, Select, Stack, TextLink, formatDateTime, toneOf, type StatusWord,
} from "@/mc";
import { panelRowWord } from "./panel/workbenchWords";
import { WORKBENCH_CHIP_LABELS } from "./scheduling/labels";
import { RequestSlideOver } from "./scheduling/RequestSlideOver";
import { workbenchSlots, type WorkbenchPlace, type WorkbenchSlotProps } from "./scheduling/slots";
import {
  HISTORY_STATUSES, KIND_CHIP, MEETING_TYPES, STATUS_ORDER, STATUS_WORD, acts, blockedRule, dueTier, parseQuery, queryString,
  hsId, queueRowWords, rowAction, workbenchInput, type StatusKey, type WorkbenchQuery,
} from "./scheduling/workbench";

const SR_ID = /^sr_[a-z0-9]+$/;

export function WorkbenchPage() {
  const route = useRoute();
  const { can } = useSession();
  const { register } = useViewState();
  const q = parseQuery(route.raw);
  const opened = route.parts[0];
  const read = useRead(["scheduling.workbench", workbenchInput(q)], () => schedulingWorkbench(workbenchInput(q)));
  // the counts strip reads the open view across the seat's whole scope, whatever the queue's filter shows
  const open = useRead(["scheduling.workbench", "open"], () => schedulingWorkbench({ view: "open", limit: 1 }));
  useEffect(
    () => register({ view: "workbench", ids: opened ? { scheduling_request: opened } : {}, summary: { count: read.data?.rows.length ?? 0 } }),
    [register, opened, read.data?.rows.length],
  );

  if (!can("scheduling.workbench")) return <NotFound route={route.raw} />;
  if (opened !== undefined && (!SR_ID.test(opened) || route.parts.length > 1)) return <NotFound route={route.raw} />;

  const base = `#workbench${queryString(q)}`;
  return (
    <Stack gap={16}>
      <PageHeader title={NAV_ITEM_LABELS.coordinatorWorkbench} />
      <Tiles q={q} tiles={open.data?.tiles} counts={open.data?.counts.by_status} loading={open.isLoading} />
      <Filters q={q} rows={read.data?.rows ?? []} />
      <Slots place="aside" query={q} rows={read.data?.rows ?? []} apply={(next) => go(`#workbench${queryString(next)}`)} />
      {read.error ? (
        <ErrorStrip
          message={RULE_LABELS.failedSyncRetryable}
          problem={{ status: read.error.status, refusal_code: read.error.code, request_id: read.error.requestId }}
          onRetry={() => void read.refetch()}
          
        />
      ) : null}
      <Queue q={q} rows={read.data?.rows ?? []} byStatus={read.data?.counts.by_status} total={read.data?.counts.total} loading={read.isLoading} />
      {opened ? <RequestSlideOver key={opened} id={opened} onClose={() => go(base)} openDialog={new URLSearchParams(route.raw.split("?")[1] ?? "").get("open")} /> : null}
    </Stack>
  );
}

/** The five counts (W-3): *To schedule*, the two Awaiting statuses, Reschedule Requested and *Scheduling SLA risk*; each
 *  filters the queue. They are the open view's counts across the seat's whole scope, whatever the filter shows. */
function Tiles({ q, tiles, counts, loading }: { q: WorkbenchQuery; tiles: Record<string, number> | undefined; counts: Record<string, number> | undefined; loading: boolean }) {
  const set = (next: Partial<WorkbenchQuery>) => go(`#workbench${queryString({ ...q, status: null, risk: false, ...next })}`);
  const tile = (status: StatusKey) => ({
    pressed: q.status === status,
    onPressedChange: (on: boolean) => set({ status: on ? status : null }),
  });
  const open = (key: string) => tiles?.[key] ?? counts?.[key];
  return (
    <KpiRow>
      <KpiSelect label={METRIC_LABELS.toSchedule} value={open("to_schedule") ?? 0} loading={loading} {...tile("requested")}  />
      <KpiSelect label={SCHEDULING_STATUS.awaitingCandidateAvailability} value={open("awaiting_candidate") ?? 0} loading={loading} {...tile("awaiting_candidate")}  />
      <KpiSelect label={SCHEDULING_STATUS.awaitingInterviewerAvailability} value={open("awaiting_interviewer") ?? 0} loading={loading} {...tile("awaiting_interviewer")}  />
      <KpiSelect label={SCHEDULING_STATUS.rescheduleRequested} value={open("reschedule_requested") ?? 0} loading={loading} {...tile("reschedule_requested")}  />
      <KpiSelect
        label={METRIC_LABELS.schedulingSlaRisk}
        value={(open("at_risk") ?? 0) + (open("breached") ?? 0)}
        loading={loading}
        pressed={q.risk}
        onPressedChange={(on) => set({ risk: on })}
        
      />
    </KpiRow>
  );
}

/** The filter bar: All, the kind chips in his §4.2 words, the state chips the read model filters on, and the requisition. */
function Filters({ q, rows }: { q: WorkbenchQuery; rows: readonly WorkbenchRowOut[] }) {
  const set = (next: Partial<WorkbenchQuery>) => go(`#workbench${queryString({ ...q, ...next })}`);
  const requisitions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const row of rows) seen.set(row.requisition_id, row.requisition_title);
    if (q.req && !seen.has(q.req)) seen.set(q.req, "");
    return [...seen.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [rows, q.req]);
  const none = !q.type && !q.status && !q.risk && !q.blocked && !q.sync && !q.req;
  return (
    <Flex gap={12} wrap>
      <Chips label={WORKBENCH_CHIP_LABELS.meetingTemplate}>
        <Chip on={none} onToggle={() => go("#workbench")} >{FILTERBAR_LABELS.all}</Chip>
        {MEETING_TYPES.map((type) => (
          <Chip key={type} on={q.type === type} onToggle={(on) => set({ type: on ? type : null })} hs={`wb.chip.${type.replace(/_/g, "-")}`}>
            {KIND_CHIP[type]}
          </Chip>
        ))}
      </Chips>
      <Chips>
        <Chip on={q.status === "reschedule_requested"} onToggle={(on) => set({ status: on ? "reschedule_requested" : null })} >
          {WORKBENCH_CHIP_LABELS.reschedules}
        </Chip>
        <Chip on={q.sync} onToggle={(on) => set({ sync: on })} >{CORE_LABELS.syncFailure}</Chip>
        <Chip on={q.blocked} onToggle={(on) => set({ blocked: on })} >{FINAL_LOOP_SCHEDULING_STATUS.blocked}</Chip>
      </Chips>
      <Select
        sm
        aria-label={NAV_ITEM_LABELS.requisitions}
        placeholder={NAV_ITEM_LABELS.requisitions}
        value={q.req ?? ""}
        options={requisitions.map(([id, title]) => ({ value: id, label: title ? `${id} · ${title}` : id }))}
        onChange={(value) => set({ req: value || null })}
        
      />
      <Slots place="filters" query={q} rows={rows} apply={(next) => go(`#workbench${queryString(next)}`)} />
    </Flex>
  );
}

/** The slots a later lane registered at this place (C-B10-13: B10's Saved views, its acknowledgment widget). */
function Slots({ place, ...props }: WorkbenchSlotProps & { place: WorkbenchPlace }) {
  const { can } = useSession();
  return (
    <>
      {workbenchSlots.at(place, can).map(({ key, component: Slot }) => <Slot key={key} {...props} />)}
    </>
  );
}

/** The eight groups in workflow order; Requested splits into Ready to Schedule and Blocked for panel/coverage (R7). */
function Queue({ q, rows, byStatus, total, loading }: {
  q: WorkbenchQuery; rows: readonly WorkbenchRowOut[]; byStatus: Record<string, number> | undefined; total: number | undefined; loading: boolean;
}) {
  const { manifest, me } = useSession();
  const actor = acts(manifest, me, "scheduling.remind");
  const [recorded, setRecorded] = useState<string | null>(null);
  const remind = useWrite(schedulingRemind, [["scheduling.workbench"], ["scheduling.open"]]);
  const [refused, setRefused] = useState<string | null>(null);

  if (loading) {
    return (
      <div aria-busy="true">
        {[0, 1, 2, 3].map((n) => <Row key={n} loading tracks={QUEUE_TRACKS} lines={QUEUE_LINES} />)}
      </div>
    );
  }
  if (total === 0 && !q.type && !q.status && !q.risk && !q.blocked && !q.sync && !q.req) {
    return <EmptyState icon="calendar">{CORE_LABELS.nothingNeedsAction}</EmptyState>;
  }
  const doRemind = async (row: WorkbenchRowOut) => {
    setRefused(null);
    try {
      await remind.mutateAsync({ scheduling_request_id: row.scheduling_request_id ?? "" });
      setRecorded(row.key);
    } catch (error) {
      setRefused(error instanceof Error ? error.message : String(error));
    }
  };
  const groups = STATUS_ORDER.filter((status) => !q.status || q.status === status);
  return (
    <Stack gap={8}>
      {recorded ? <Recorded live /> : null}
      {refused ? <Banner tone="red" live>{refused}</Banner> : null}
      {groups.map((status) => {
        const mine = rows.filter((row) => row.status === status);
        const count = byStatus?.[status] ?? mine.length;
        const body = status === "requested" ? (
          <>
            <SubGroup title={FINAL_LOOP_SCHEDULING_STATUS.readyToSchedule} rows={mine.filter((row) => !row.blocked_reason)} actor={actor} onRemind={doRemind} busy={remind.isPending} />
            <SubGroup title={FINAL_LOOP_SCHEDULING_STATUS.blocked} rows={mine.filter((row) => row.blocked_reason)} actor={actor} onRemind={doRemind} busy={remind.isPending} />
          </>
        ) : (
          <Rows rows={mine} actor={actor} onRemind={doRemind} busy={remind.isPending} />
        );
        return (
          <QueueGroup
            key={status}
            status={STATUS_WORD[status]}
            count={count}
            memoryKey={`wb.${status}`}
            defaultOpen={!HISTORY_STATUSES.has(status)}
            hs={`wb.group.${status.replace(/_/g, "-")}`}
          >
            {count === 0 ? <Paragraph sm>{CORE_LABELS.nothingNeedsAction}</Paragraph> : body}
          </QueueGroup>
        );
      })}
    </Stack>
  );
}

function SubGroup({ title, rows, actor, onRemind, busy }: { title: string; rows: readonly WorkbenchRowOut[]; actor: boolean; onRemind: (row: WorkbenchRowOut) => void; busy: boolean }) {
  if (rows.length === 0) return null;
  return (
    <section aria-label={title}>
      <SectionTitle as="h4" count={rows.length}>{title}</SectionTitle>
      <Rows rows={rows} actor={actor} onRemind={onRemind} busy={busy} />
    </section>
  );
}

function Rows({ rows, actor, onRemind, busy }: { rows: readonly WorkbenchRowOut[]; actor: boolean; onRemind: (row: WorkbenchRowOut) => void; busy: boolean }) {
  return (
    <div>
      {rows.map((row) => <QueueRow key={row.key} row={row} actor={actor} onRemind={onRemind} busy={busy} />)}
    </div>
  );
}

/** The row's tracks (DC-235, F-150): what · when share the text, due · status · flags · action are sized by their pills,
 * so the due pill and the status pill never overlap; under 820 px of queue a row draws what · status · action, then
 * when · due · flags. */
const QUEUE_TRACKS = ["lead", "wide", "fit", "fit", "fit", "fit"] as const;
const QUEUE_LINES = [[0, 3, 5], [1, 2, 4]] as const;

/** One request: What (template, then the candidate and the requisition as the link) · When · Due · Status · Flags · Action. */
function QueueRow({ row, actor, onRemind, busy }: { row: WorkbenchRowOut; actor: boolean; onRemind: (row: WorkbenchRowOut) => void; busy: boolean }) {
  const tz = row.event_tz ?? undefined;
  const status = panelRowWord(row) ?? (STATUS_WORD as Record<string, string>)[row.status] ?? row.status;
  const ended = HISTORY_STATUSES.has(row.status as StatusKey);
  const action = actor && !ended ? rowAction(row) : null;
  const rule = blockedRule(row);
  const href = row.route ?? (row.scheduling_request_id ? `#workbench/${row.scheduling_request_id}` : undefined);
  // DC-241 (F-162): a request whose reschedule was asked reads "Reschedule request · {meeting} · {requisition id}"
  const { name, sub } = queueRowWords(row);
  const slug = hsId(row.scheduling_request_id ?? row.key);
  return (
    <Row tracks={QUEUE_TRACKS} lines={QUEUE_LINES}>
      <RowName
        name={href ? <TextLink href={href} >{name}</TextLink> : name}
        sub={sub || undefined}
      />
      <span>{row.next_start ? formatDateTime(row.next_start, tz) : "—"}</span>
      <span>
        {row.due_at && !ended ? (
          <Due
            tier={dueTier(row.risk)}
            phrase={formatDateTime(row.due_at, tz)}
            at={row.due_at}
            calendar={`${SLA_LABELS.activeTime} · ${RULE_LABELS.slaUsesActiveTime}`}
          />
        ) : null}
      </span>
      <span><Pill tone={toneOf(status as StatusWord)}>{status}</Pill></span>
      <span>
        {row.conflicts_n > 0 ? <Pill tone="flag">{SCHEDULING_LABELS.conflict}</Pill> : null}
        {row.sync_failed ? <Pill tone="red">{CORE_LABELS.syncFailure}</Pill> : null}
      </span>
      <span>
        {action === null ? null : action.kind === "remind" ? (
          <Button sm tone="ghost" busy={busy} onClick={() => onRemind(row)} >{action.label}</Button>
        ) : (
          <Button
            sm
            tone={action.kind === "page" ? "primary" : "ghost"}
            blocked={rule && row.status === "requested" ? { reason: rule } : undefined}
            onClick={() => go(action.href)}
            
          >
            {action.label}
          </Button>
        )}
      </span>
    </Row>
  );
}
